import type { NavMenuItem } from './types';
import type { RailId } from './types';
import { menuItemsByRail, platformMenuItems } from './menuByRail';
import { NAV_RAILS, ORG_HOME_PATH } from './rails';
import type { NavRailDef } from './types';

type HasPermission = (permission: string) => boolean;

export type NavAccessOpts = {
  role?: string;
  hasPermission: HasPermission;
  /** When omitted, plan gates are skipped (tests and sessions still loading). */
  entitlements?: { features: readonly string[] };
};

const planAllowsItem = (item: NavMenuItem, opts: NavAccessOpts): boolean => {
  if (!item.feature || !opts.entitlements) return true;
  return opts.entitlements.features.includes(item.feature);
};

export const checkNavItemAccess = (item: NavMenuItem, opts: NavAccessOpts): boolean => {
  const { role, hasPermission } = opts;
  if (!role) return false;
  if (role === 'super_admin') return true;
  if (!planAllowsItem(item, opts)) return false;
  if (role === 'admin') return true;
  if (item.permissions && item.permissions.length > 0) {
    return item.permissions.some((p) => hasPermission(p));
  }
  return true;
};

export const filterNavItems = (items: NavMenuItem[], opts: NavAccessOpts): NavMenuItem[] =>
  items.filter((item) => checkNavItemAccess(item, opts));

export const getRailMenuItems = (railId: RailId, opts: NavAccessOpts): NavMenuItem[] =>
  filterNavItems(menuItemsByRail[railId] ?? [], opts);

export const getVisibleRails = (opts: NavAccessOpts): NavRailDef[] => {
  if (opts.role === 'super_admin') return [];
  return NAV_RAILS.filter((rail) => {
    if (rail.id === 'work') {
      if (opts.role === 'admin') return true;
      return opts.hasPermission('task:read') || opts.hasPermission('project:read');
    }
    return getRailMenuItems(rail.id, opts).length > 0;
  });
};

export const getPlatformMenuItems = (opts: NavAccessOpts): NavMenuItem[] =>
  filterNavItems(platformMenuItems, opts);

/** First path the user can open within a rail (for rail icon click). */
export const getRailDefaultPath = (railId: RailId, opts: NavAccessOpts): string => {
  const rail = NAV_RAILS.find((r) => r.id === railId);
  if (railId === 'work') {
    if (opts.role === 'admin' || opts.hasPermission('task:read')) {
      return '/tasks?view=list&mine=0';
    }
    if (opts.hasPermission('project:read')) {
      return '/projects';
    }
  }
  const items = getRailMenuItems(railId, opts);
  return items[0]?.path ?? rail?.defaultPath ?? ORG_HOME_PATH;
};
